import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import "./LeadRecord.css";

/* ================= API ================= */
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.magnirauniverse.com";

/* ================= CONSTANTS ================= */
const STAGES = [
  "New",
  "Working",
  "Meeting Done",
  "Follow-up",
  "Quote Shared",
  "Waiting Closure",
  "Closed - Lost",
  "Closed - Won",
];

const LOST_REASONS = [
  "Low Budget",
  "Signed up with other competitor",
  "After multiple follow-ups not responding",
  "Needed only quotation",
  "Not interested",
];

export default function LeadRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  /* ================= STATE ================= */
  const [lead, setLead] = useState(null);
  const [tab, setTab] = useState("Activity & Chatter");
  const [quotes, setQuotes] = useState([]);
  const [approvedValue, setApprovedValue] = useState(0);

  // Stage
  const [selectedStage, setSelectedStage] = useState("");
  const [lostReason, setLostReason] = useState("");

  // Dates
  const [followUpDate, setFollowUpDate] = useState("");
  const [meetingScheduleDate, setMeetingScheduleDate] = useState("");

  // Chatter
  const [comment, setComment] = useState("");

  // Details
  const [clientDetails, setClientDetails] = useState({});
  const [salesManagers, setSalesManagers] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [assignedSalesManager, setAssignedSalesManager] = useState("");
  const [assignedDesigner, setAssignedDesigner] = useState("");

  const formatINR = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return value
      .toString()
      .replace(/,/g, "")
      .replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  };

  const unformatINR = (value) => (value || "").replace(/,/g, "");

  /* ================= LOAD QUOTES ================= */
  const loadQuotes = async (projectName, currentLead = null) => {
    if (!projectName) {
      setQuotes([]);
      setApprovedValue(0);
      return currentLead;
    }

    try {
      const res = await axios.get(
        `${BASE_URL.replace("/api", "")}/api/quotes?projectName=${encodeURIComponent(projectName)}`,
      );

      const quoteData = res.data || [];
      setQuotes(quoteData);

      const approved = quoteData.find((q) => q.status === "Approved");

      if (approved) {
        const approvedAmount = Number(approved.finalAmount || 0);
        setApprovedValue(approvedAmount);

        // save only if different
        if (Number(currentLead?.projectValue || 0) !== approvedAmount) {
          const updateRes = await axios.put(
            `${BASE_URL}/api/leads/${id}/project-value`,
            {
              projectValue: approvedAmount,
            },
          );

          return updateRes.data?.lead || currentLead;
        }
      } else {
        setApprovedValue(0);
      }

      return currentLead;
    } catch (err) {
      console.error("Quote fetch failed", err);
      return currentLead;
    }
  };

  /* ================= LOAD LEAD ================= */
  const loadLead = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/leads/${id}`);
      let leadData = res.data;

      setLead(leadData);
      setSelectedStage(leadData.status || "");
      setClientDetails(leadData.clientDetails || {});
      setAssignedSalesManager(leadData.assignedSalesManager?._id || "");
      setAssignedDesigner(leadData.assignedDesigner?._id || "");

      leadData = await loadQuotes(
        leadData.projectName || leadData.leadName,
        leadData,
      );

      if (leadData) {
        setLead(leadData);
        setSelectedStage(leadData.status || "");
        setClientDetails(leadData.clientDetails || {});
        setAssignedSalesManager(leadData.assignedSalesManager?._id || "");
        setAssignedDesigner(leadData.assignedDesigner?._id || "");
      }
    } catch (err) {
      console.error("Error loading lead:", err);
    }
  };

  useEffect(() => {
    loadLead();
  }, [id]);

  /* ================= LOAD USERS ================= */
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const sales = await axios.get(
          `${BASE_URL}/api/leads/users/list?role=sales_manager`,
        );
        const des = await axios.get(
          `${BASE_URL}/api/leads/users/list?role=designer`,
        );

        setSalesManagers(sales.data || []);
        setDesigners(des.data || []);
      } catch (err) {
        console.error("Error loading users:", err);
      }
    };

    loadUsers();
  }, []);

  /* ================= STAGE UPDATE ================= */
  const updateStage = async () => {
    if (!lead) return;
    if (selectedStage === lead.status) return;

    if (selectedStage === "Closed - Lost" && !lostReason) {
      alert("Please select lost reason");
      return;
    }

    if (
      selectedStage === "Closed - Won" &&
      Number(lead?.projectValue || approvedValue || 0) <= 0
    ) {
      alert("Approved quote amount not found. Please approve quote first.");
      return;
    }

    try {
      await axios.put(`${BASE_URL}/api/leads/${id}/stage`, {
        status: selectedStage,
        lostReason,
        userId: user?._id,
      });

      setLostReason("");
      await loadLead();
    } catch (err) {
      console.error("Stage update error:", err);
      alert(err?.response?.data?.message || "Failed to update stage");
    }
  };

  /* ================= FOLLOW-UP ================= */
  const addFollowUp = async () => {
    if (!followUpDate) return;

    try {
      await axios.put(`${BASE_URL}/api/leads/${id}/follow-up`, {
        followUpDate,
        userId: user?._id,
      });

      setFollowUpDate("");
      await loadLead();
    } catch (err) {
      console.error("Follow-up update error:", err);
      alert(err?.response?.data?.message || "Failed to update follow-up");
    }
  };

  /* ================= MEETING SCHEDULE ================= */
  const scheduleMeeting = async () => {
    if (!meetingScheduleDate) return;

    const selected = new Date(meetingScheduleDate);
    if (selected <= new Date()) {
      alert("Meeting must be in future");
      return;
    }

    try {
      await axios.put(`${BASE_URL}/api/leads/${id}/meeting/schedule`, {
        meetingDate: meetingScheduleDate,
        userId: user?._id,
      });

      setMeetingScheduleDate("");
      await loadLead();
    } catch (err) {
      console.error("Meeting schedule error:", err);
      alert(err?.response?.data?.message || "Failed to schedule meeting");
    }
  };

  /* ================= COMMENT ================= */
  const postComment = async () => {
    if (!comment.trim()) return;

    try {
      await axios.put(`${BASE_URL}/api/leads/${id}/comment`, {
        text: comment,
        userId: user?._id,
      });

      setComment("");
      await loadLead();
    } catch (err) {
      console.error("Comment post error:", err);
      alert(err?.response?.data?.message || "Failed to post comment");
    }
  };

  /* ================= SAVE DETAILS ================= */
  const saveDetails = async () => {
    try {
      await axios.put(`${BASE_URL}/api/leads/${id}/details`, {
        clientDetails,
        assignedSalesManager,
        assignedDesigner,
      });

      alert("✅ Details saved successfully");
      await loadLead();
    } catch (err) {
      console.error("Save details error:", err);
      alert(err?.response?.data?.message || "Failed to save details");
    }
  };

  if (!lead) return null;

  const currentStageIndex = STAGES.indexOf(lead.status);

  /* ================= UI ================= */
  return (
    <div className="sf-record-page">
      {/* ================= STICKY HEADER ================= */}
      <div className="sf-record-header sticky">
        <div>
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Leads
          </button>

          <h2>{clientDetails.ClientName || lead.leadName}</h2>
          <p>
            {clientDetails.email || "-"} | {clientDetails.phoneNumber || "-"}
          </p>

          <div className="lead-top-dates">
            <div>
              <b>📞 Scheduled Meeting:</b>{" "}
              {lead.meetingDate
                ? new Date(lead.meetingDate).toLocaleString()
                : "Not Scheduled"}
              <input
                type="datetime-local"
                value={meetingScheduleDate}
                onChange={(e) => setMeetingScheduleDate(e.target.value)}
              />
              <button onClick={scheduleMeeting}>Schedule</button>
            </div>

            <div>
              <b>📅 Follow-up</b>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
              />
              <button onClick={addFollowUp}>Update</button>
            </div>
          </div>
        </div>

        <div className="sf-stage-box">
          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
          >
            {STAGES.map((s, index) => (
              <option key={s} value={s} disabled={index < currentStageIndex}>
                {s}
              </option>
            ))}
          </select>

          {selectedStage === "Closed - Lost" && (
            <select
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
            >
              <option value="">Select Lost Reason</option>
              {LOST_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}

          <button className="sf-btn primary" onClick={updateStage}>
            Update Stage
          </button>
        </div>
      </div>

      {/* ================= STAGE PATH ================= */}
      <div className="sf-path sticky">
        {STAGES.map((s, i) => (
          <div
            key={s}
            className={`sf-path-step ${i <= currentStageIndex ? "completed" : ""}`}
          >
            {s}
          </div>
        ))}
      </div>

      {/* ================= BODY ================= */}
      <div className="sf-record-body">
        <div className="sf-main scrollable">
          {/* TABS */}
          <div className="sf-tabs sticky-tabs">
            {["Activity & Chatter", "Details", "Quotes"].map((t) => (
              <span
                key={t}
                className={tab === t ? "active" : ""}
                onClick={() => setTab(t)}
              >
                {t}
              </span>
            ))}
          </div>

          {/* ================= ACTIVITY ================= */}
          {tab === "Activity & Chatter" && (
            <div className="sf-activity-layout">
              <div className="sf-card activity-panel">
                <h4>Activity</h4>
                {lead.activities?.length ? (
                  lead.activities.map((a, i) => (
                    <div key={i} className="activity-item">
                      <b>{a.type}</b>
                      <p>{a.message}</p>
                      <small>{new Date(a.createdAt).toLocaleString()}</small>
                    </div>
                  ))
                ) : (
                  <p>No activity yet</p>
                )}
              </div>

              <div className="sf-card chatter-panel">
                <h4>Chatter</h4>

                {lead.activities
                  ?.filter((a) => a.type === "COMMENT")
                  .map((c, i) => (
                    <div key={i} className="chatter-item">
                      <p>{c.message}</p>
                      <small>{new Date(c.createdAt).toLocaleString()}</small>
                    </div>
                  ))}

                <textarea
                  placeholder="Write a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />

                <button className="sf-btn primary" onClick={postComment}>
                  Post
                </button>
              </div>
            </div>
          )}

          {/* ================= DETAILS ================= */}
          {tab === "Details" && (
            <div className="sf-card">
              <h4>Client Details</h4>

              <div className="details-grid">
                {[
                  "ClientName",
                  "phoneNumber",
                  "email",
                  "clientAddress",
                  "siteAddress",
                  "dcId",
                  "possessionDate",
                  "expectedCompletionDate",
                  "panCardNumber",
                  "gstNumber",
                ].map((f) => (
                  <div key={f}>
                    <label>{f.replace(/([A-Z])/g, " $1")}</label>
                    <input
                      type={f.toLowerCase().includes("date") ? "date" : "text"}
                      value={clientDetails[f] || ""}
                      onChange={(e) =>
                        setClientDetails((p) => ({
                          ...p,
                          [f]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}

                <div>
                  <label>Client Budget (₹)</label>
                  <input
                    type="text"
                    placeholder="Enter budget (e.g. 10,00,000)"
                    value={formatINR(clientDetails.clientBudget)}
                    onChange={(e) => {
                      const rawValue = unformatINR(e.target.value);
                      if (!isNaN(rawValue)) {
                        setClientDetails((p) => ({
                          ...p,
                          clientBudget: rawValue,
                        }));
                      }
                    }}
                  />
                </div>
              </div>

              <h4 style={{ marginTop: 16 }}>Assign Team</h4>

              <div className="details-grid">
                <div>
                  <label>Designer</label>
                  <select
                    value={assignedDesigner}
                    onChange={(e) => setAssignedDesigner(e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {designers.map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button className="sf-btn primary" onClick={saveDetails}>
                Save Details
              </button>
            </div>
          )}

          {/* ================= QUOTES ================= */}
          {tab === "Quotes" && (
            <div className="sf-card" style={{ marginBottom: "20px" }}>
              <h4>Project Quotes</h4>

              {quotes.length ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={tableHead}>Quote No</th>
                      <th style={tableHead}>Date</th>
                      <th style={tableHead}>Total Value</th>
                      <th style={tableHead}>Final Amount</th>
                      <th style={tableHead}>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {quotes.map((q, index) => (
                      <tr key={q._id}>
                        <td style={tableCell}>{index + 1}</td>
                        <td style={tableCell}>
                          {q.date
                            ? new Date(q.date).toLocaleDateString("en-IN")
                            : "-"}
                        </td>
                        <td style={tableCell}>
                          ₹ {q.totalMRP?.toLocaleString("en-IN")}
                        </td>
                        <td style={tableCell}>
                          ₹ {q.finalAmount?.toLocaleString("en-IN")}
                        </td>
                        <td style={tableCell}>
                          <span
                            style={{
                              padding: "5px 10px",
                              borderRadius: "4px",
                              color: "#fff",
                              background:
                                q.status === "Approved" ? "green" : "#dc3545",
                            }}
                          >
                            {q.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No quotes created yet.</p>
              )}

              {approvedValue > 0 && (
                <div
                  style={{
                    marginTop: "15px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#2e7d32",
                  }}
                >
                  Project Value (Approved Quote): ₹{" "}
                  {approvedValue.toLocaleString("en-IN")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* TABLE STYLES */
const tableHead = {
  padding: "8px",
  border: "1px solid #ddd",
  background: "#f5f5f5",
};

const tableCell = {
  padding: "8px",
  border: "1px solid #ddd",
  textAlign: "center",
};
